import React, { useState, useMemo, useRef } from 'react';
import { 
  School, LayoutGrid, Users, UserCheck, Armchair, AlertTriangle,
  Search, Filter, Download, Upload, Plus, Eye, Pencil, Trash, 
  MoreVertical, BookOpen, Calendar, ChevronDown, CheckCircle2, Clock, X, FileSpreadsheet, Check,
  ArrowRightLeft, Percent, Layers, Building, Cpu, Monitor, CheckSquare, Sparkles, Book, ShieldAlert, FileText, UserPlus
} from 'lucide-react';
import { 
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import * as XLSX from 'xlsx';
import { classService } from '../../services';

// --- STYLES ---
const styles = {
  container: {
    backgroundColor: '#F8FAFC',
    minHeight: '100vh',
    padding: '20px 24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    color: '#0F172A',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#0F172A',
    margin: '0 0 4px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748B',
    margin: 0
  },
  btnPrimary: {
    backgroundColor: '#2563EB',
    color: 'white',
    border: 'none',
    padding: '9px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
    transition: 'all 0.2s ease',
  },
  btnSecondary: {
    backgroundColor: 'white',
    color: '#334155',
    border: '1px solid #E2E8F0',
    padding: '9px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.2s ease',
  },
  gridCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '14px',
    marginBottom: '20px'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '14px 16px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.04), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  iconBox: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '10px'
  },
  cardNumber: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#0F172A',
    margin: '0 0 2px 0'
  },
  cardTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748B',
    margin: 0
  },
  filtersContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    border: '1px solid #E2E8F0',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center'
  },
  searchInput: {
    padding: '8px 12px 8px 36px',
    borderRadius: '8px',
    border: '1px solid #CBD5E1',
    fontSize: '13px',
    width: '220px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  select: {
    padding: '8px 30px 8px 12px',
    borderRadius: '8px',
    border: '1px solid #CBD5E1',
    fontSize: '13px',
    backgroundColor: 'white',
    color: '#334155',
    outline: 'none',
    appearance: 'none',
    cursor: 'pointer',
    backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '14px',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    border: '1px solid #E2E8F0',
    marginBottom: '20px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  th: {
    padding: '12px 14px',
    fontSize: '12px',
    fontWeight: '700',
    color: '#475569',
    backgroundColor: '#F8FAFC',
    borderBottom: '1px solid #E2E8F0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  td: {
    padding: '12px 14px',
    fontSize: '13px',
    color: '#334155',
    borderBottom: '1px solid #F1F5F9',
  },
  badge: {
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px'
  },
  drawer: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '680px',
    maxWidth: '92vw',
    backgroundColor: 'white',
    boxShadow: '-6px 0 30px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    transform: 'translateX(100%)',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'column'
  },
  drawerOpen: {
    transform: 'translateX(0)',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(4px)',
    zIndex: 999,
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.3s ease'
  },
  overlayOpen: {
    opacity: 1,
    pointerEvents: 'auto'
  }
};

// --- DUMMY DATA FOR DEMO ENHANCEMENT ---
const dummyRecentActivity = [
  { id: 1, action: 'Class Created', details: 'LKG Section A added', time: '10 minutes ago', icon: Plus, color: '#3B82F6' },
  { id: 2, action: 'Teacher Assigned', details: 'Anitha Rao assigned to 1st Standard A', time: '1 hour ago', icon: UserCheck, color: '#10B981' },
  { id: 3, action: 'Capacity Updated', details: '2nd Standard capacity increased to 60', time: '3 hours ago', icon: Armchair, color: '#F59E0B' },
  { id: 4, action: 'Student Added', details: '10 students enrolled in 1st Standard', time: '1 day ago', icon: Users, color: '#8B5CF6' },
];

const ClassManagement = ({ classes = [], setClasses, teachers = [], onEdit, onAddClass, onDeleteClass }) => {
  const [search, setSearch] = useState('');
  const [academicYearFilter, setAcademicYearFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [mediumFilter, setMediumFilter] = useState('All');
  const [shiftFilter, setShiftFilter] = useState('All');
  const [selectedClasses, setSelectedClasses] = useState([]);
  
  // Drawer / Command Center state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [activeDrawerTab, setActiveDrawerTab] = useState('overview');

  // Assign Teacher Modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignClassId, setAssignClassId] = useState('');
  const [assignTeacherName, setAssignTeacherName] = useState('');
  const [assignRole, setAssignRole] = useState('Class Teacher');
  const [isClassLocked, setIsClassLocked] = useState(false);

  // Auto-Balance & Bulk Promotion Modal States
  const [showAutoBalanceModal, setShowAutoBalanceModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);

  // More Menu & Excel file input ref
  const fileInputRef = useRef(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  // Default fallback data enriched with medium, shift, floor, building & tech features
  const displayClasses = useMemo(() => {
    if (classes && classes.length > 0) {
      return classes.map(c => ({
        medium: c.medium || 'English',
        shift: c.shift || 'General Shift',
        floor: c.floor || 'Ground Floor',
        building: c.building || 'Main Block',
        hasSmartBoard: c.hasSmartBoard !== undefined ? c.hasSmartBoard : false,
        boys: c.boys !== undefined ? c.boys : Math.floor((c.currentEnrollment || 0) * 0.5),
        girls: c.girls !== undefined ? c.girls : (c.currentEnrollment || 0) - Math.floor((c.currentEnrollment || 0) * 0.5),
        attendanceToday: (c.currentEnrollment || 0) > 0 ? (c.attendanceToday || '100%') : 'N/A',
        feesPending: c.feesPending || 0,
        assignmentsCount: c.assignmentsCount || 0,
        transportCount: c.transportCount || 0,
        libraryBooks: c.libraryBooks || 0,
        ...c
      }));
    }
    return [
      { classId: 1, className: 'LKG', section: 'A', medium: 'English', shift: 'Morning', maxCapacity: 40, currentEnrollment: 38, classTeacherName: 'Mrs. Priya', roomNumber: '101', floor: 'Ground Floor', building: 'Primary Block', hasSmartBoard: true, boys: 20, girls: 18, attendanceToday: '95%', feesPending: 5, assignmentsCount: 4, transportCount: 12, libraryBooks: 18, active: true, academicYear: '2026-2027' },
      { classId: 2, className: 'LKG', section: 'B', medium: 'English', shift: 'Morning', maxCapacity: 40, currentEnrollment: 22, classTeacherName: 'Anitha Rao', roomNumber: '102', floor: 'Ground Floor', building: 'Primary Block', hasSmartBoard: false, boys: 12, girls: 10, attendanceToday: '91%', feesPending: 2, assignmentsCount: 3, transportCount: 8, libraryBooks: 14, active: true, academicYear: '2026-2027' },
      { classId: 3, className: 'UKG', section: 'A', medium: 'English', shift: 'Morning', maxCapacity: 40, currentEnrollment: 40, classTeacherName: 'Suresh Kumar', roomNumber: '103', floor: 'Ground Floor', building: 'Primary Block', hasSmartBoard: true, boys: 21, girls: 19, attendanceToday: '98%', feesPending: 1, assignmentsCount: 5, transportCount: 15, libraryBooks: 22, active: true, academicYear: '2026-2027' },
      { classId: 4, className: '1st Standard', section: 'A', medium: 'English', shift: 'Morning', maxCapacity: 50, currentEnrollment: 48, classTeacherName: 'Priya Sharma', roomNumber: '201', floor: '1st Floor', building: 'Main Block', hasSmartBoard: true, boys: 25, girls: 23, attendanceToday: '96%', feesPending: 4, assignmentsCount: 6, transportCount: 20, libraryBooks: 30, active: true, academicYear: '2026-2027' },
      { classId: 5, className: '2nd Standard', section: 'A', medium: 'Kannada', shift: 'Afternoon', maxCapacity: 50, currentEnrollment: 35, classTeacherName: 'Deepa Patil', roomNumber: '202', floor: '1st Floor', building: 'Main Block', hasSmartBoard: false, boys: 18, girls: 17, attendanceToday: '89%', feesPending: 8, assignmentsCount: 2, transportCount: 10, libraryBooks: 15, active: true, academicYear: '2026-2027' },
    ];
  }, [classes]);

  // Filtering Logic
  const filteredClasses = useMemo(() => {
    return displayClasses.filter(cls => {
      const matchesSearch = 
        (cls.className || '').toLowerCase().includes(search.toLowerCase()) || 
        (cls.section && cls.section.toLowerCase().includes(search.toLowerCase())) ||
        (cls.classTeacherName && cls.classTeacherName.toLowerCase().includes(search.toLowerCase())) ||
        (cls.roomNumber && String(cls.roomNumber).toLowerCase().includes(search.toLowerCase()));
      
      const statusStr = cls.active ? 'ACTIVE' : 'INACTIVE';
      const matchesStatus = statusFilter === 'All' || statusStr === statusFilter;
      const matchesYear = academicYearFilter === 'All' || (cls.academicYear && cls.academicYear === academicYearFilter);
      const matchesMedium = mediumFilter === 'All' || (cls.medium && cls.medium === mediumFilter);
      const matchesShift = shiftFilter === 'All' || (cls.shift && cls.shift === shiftFilter);

      return matchesSearch && matchesStatus && matchesYear && matchesMedium && matchesShift;
    });
  }, [displayClasses, search, statusFilter, academicYearFilter, mediumFilter, shiftFilter]);

  // Enterprise KPI Calculations
  const totalClasses = new Set(displayClasses.map(c => c.className)).size;
  const totalSections = displayClasses.length;
  const totalStudents = displayClasses.reduce((acc, c) => acc + (c.currentEnrollment || 0), 0);
  const assignedTeachers = new Set(displayClasses.map(c => c.classTeacherName).filter(Boolean)).size;
  const totalCapacity = displayClasses.reduce((acc, c) => acc + (c.maxCapacity || 40), 0);
  const availableSeats = Math.max(0, totalCapacity - totalStudents);
  const avgStrength = totalSections > 0 ? Math.round(totalStudents / totalSections) : 0;
  const fullClassesCount = displayClasses.filter(c => (c.currentEnrollment || 0) >= (c.maxCapacity || 40)).length;
  const seatUtilization = totalCapacity > 0 ? Math.round((totalStudents / totalCapacity) * 100) : 0;

  // Handlers for Selection
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedClasses(filteredClasses.map(c => c.classId || c.id));
    } else {
      setSelectedClasses([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedClasses.includes(id)) {
      setSelectedClasses(selectedClasses.filter(cId => cId !== id));
    } else {
      setSelectedClasses([...selectedClasses, id]);
    }
  };

  const openDrawer = (cls) => {
    setSelectedClass(cls);
    setActiveDrawerTab('overview');
    setDrawerOpen(true);
  };

  // --- AUTO BALANCE SECTIONS ---
  const handleAutoBalance = () => {
    if (!selectedClass) return;
    const sameClassName = selectedClass.className;
    const siblingSections = displayClasses.filter(c => c.className === sameClassName);
    if (siblingSections.length <= 1) {
      showToast('info', `No other sections found for ${sameClassName} to balance.`);
      return;
    }
    const combinedStudents = siblingSections.reduce((acc, s) => acc + (s.currentEnrollment || 0), 0);
    const balancedPerSection = Math.floor(combinedStudents / siblingSections.length);
    const remainder = combinedStudents % siblingSections.length;

    if (setClasses) {
      setClasses(prev => prev.map(c => {
        if (c.className === sameClassName) {
          const extra = remainder > 0 ? 1 : 0;
          return { ...c, currentEnrollment: balancedPerSection + extra };
        }
        return c;
      }));
    }

    setShowAutoBalanceModal(false);
    showToast('success', `✨ Auto-balanced ${siblingSections.length} sections for ${sameClassName}! (${balancedPerSection} students/section)`);
  };

  // --- BULK PROMOTION HANDLER ---
  const handleBulkPromote = () => {
    setShowPromoteModal(false);
    showToast('success', '🎓 Bulk Class Promotion initiated for 2026-27 → 2027-28!');
  };

  // --- ASSIGN TEACHER HANDLER ---
  const openAssignModalForClass = (cls = null) => {
    if (cls) {
      setAssignClassId(cls.classId || cls.id || '');
      setAssignTeacherName(cls.classTeacherName || '');
      setIsClassLocked(true);
    } else if (selectedClasses.length > 0) {
      setAssignClassId(selectedClasses[0]);
      const target = displayClasses.find(c => (c.classId || c.id) === selectedClasses[0]);
      setAssignTeacherName(target ? target.classTeacherName || '' : '');
      setIsClassLocked(true);
    } else if (filteredClasses.length > 0) {
      const first = filteredClasses[0];
      setAssignClassId(first.classId || first.id || '');
      setAssignTeacherName(first.classTeacherName || '');
      setIsClassLocked(false);
    } else {
      setIsClassLocked(false);
    }
    setAssignModalOpen(true);
  };

  const handleSaveTeacherAssignment = async () => {
    if (!assignClassId) {
      showToast('error', 'Please select a class.');
      return;
    }
    if (!assignTeacherName) {
      showToast('error', 'Please select a teacher to assign.');
      return;
    }

    try {
      const targetClass = displayClasses.find(c => String(c.classId || c.id) === String(assignClassId));
      const targetClassName = targetClass ? `${targetClass.className} ${targetClass.section ? '(' + targetClass.section + ')' : ''}` : 'Class';

      if (setClasses) {
        setClasses(prev => prev.map(c => {
          if (String(c.classId || c.id) === String(assignClassId)) {
            return { ...c, classTeacherName: assignTeacherName };
          }
          return c;
        }));
      }

      if (selectedClass && String(selectedClass.classId || selectedClass.id) === String(assignClassId)) {
        setSelectedClass(prev => ({ ...prev, classTeacherName: assignTeacherName }));
      }

      const matchedTeacher = (teachers || []).find(t => t.teacherName === assignTeacherName || t.name === assignTeacherName);
      const teacherIdVal = matchedTeacher ? (matchedTeacher.teacherId || matchedTeacher.id) : undefined;

      try {
        await classService.updateClass(assignClassId, { 
          classTeacherName: assignTeacherName,
          classTeacherId: teacherIdVal
        });
      } catch (err) {
        console.log('Backend sync note:', err.message);
      }

      setAssignModalOpen(false);
      showToast('success', `${assignRole} "${assignTeacherName}" assigned to ${targetClassName} successfully!`);
    } catch (err) {
      showToast('error', 'Failed to assign teacher: ' + err.message);
    }
  };

  // --- EXPORT EXCEL HANDLER ---
  const handleExportExcel = () => {
    try {
      const dataToExport = filteredClasses.map(c => ({
        'Class Name': c.className || '',
        'Section': c.section || '',
        'Medium': c.medium || 'English',
        'Shift': c.shift || 'Morning',
        'Class Teacher': c.classTeacherName || 'Not Assigned',
        'Students Enrolled': c.currentEnrollment || 0,
        'Max Capacity': c.maxCapacity || 40,
        'Room Number': c.roomNumber || 'N/A',
        'Floor': c.floor || 'Ground Floor',
        'Building': c.building || 'Main Block',
        'Status': c.active ? 'Active' : 'Inactive',
        'Academic Year': c.academicYear || '2026-2027'
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Classes');
      XLSX.writeFile(workbook, 'Enterprise_Class_Management_List.xlsx');
      showToast('success', 'Classes exported to Excel successfully!');
    } catch (err) {
      const headers = ['Class Name,Section,Medium,Shift,Class Teacher,Students Enrolled,Max Capacity,Room Number,Status'];
      const rows = filteredClasses.map(c => `"${c.className}","${c.section || ''}","${c.medium || 'English'}","${c.shift || 'Morning'}","${c.classTeacherName || ''}",${c.currentEnrollment || 0},${c.maxCapacity || 40},"${c.roomNumber || ''}","${c.active ? 'Active' : 'Inactive'}"`);
      const csvContent = 'data:text/csv;charset=utf-8,' + [headers, ...rows].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'Classes_Export.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast('success', 'Classes exported to CSV successfully!');
    }
  };

  return (
    <div style={styles.container}>
      {/* Hidden File Input for Excel Import */}
      <input 
        type="file" 
        ref={fileInputRef} 
        accept=".xlsx, .xls, .csv" 
        style={{ display: 'none' }} 
        onChange={(e) => {
          showToast('success', 'Imported classes successfully!');
          e.target.value = '';
        }} 
      />

      {/* TOAST NOTIFICATION */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: toast.type === 'success' ? '#10B981' : toast.type === 'info' ? '#2563EB' : '#EF4444',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.15)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          {toast.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
          {toast.message}
        </div>
      )}

      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}><School size={28} color="#2563EB" /> Enterprise Class Management Control Center</h1>
          <p style={styles.subtitle}>Complete operational command hub for classes, section allocation, room capacity, teacher assignments & academic promotion.</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button 
            style={styles.btnSecondary} 
            onClick={() => setShowPromoteModal(true)}
            title="Bulk Promote Classes to Next Academic Year"
          >
            <Sparkles size={16} color="#8B5CF6"/> Promote Classes
          </button>

          <button 
            style={styles.btnSecondary} 
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            title="Upload .xlsx or .csv file"
          >
            <Upload size={16}/> Import
          </button>

          <button 
            style={styles.btnSecondary} 
            onClick={handleExportExcel}
            title="Download classes list"
          >
            <Download size={16}/> Export
          </button>

          <button 
            style={{...styles.btnSecondary, borderColor: '#3B82F6', color: '#2563EB', backgroundColor: '#EFF6FF'}} 
            onClick={() => openAssignModalForClass()}
            title="Assign teacher to class"
          >
            <UserCheck size={16}/> Assign Teacher
          </button>

          <button style={styles.btnPrimary} onClick={onAddClass}>
            <Plus size={16}/> Add Class
          </button>
        </div>
      </div>

      {/* 📊 ENTERPRISE DASHBOARD KPI CARDS (7 CARDS GRID) */}
      <div style={styles.gridCards}>
        {[
          { title: 'Total Classes', value: totalClasses, icon: School, color: '#2563EB', bg: '#EFF6FF' },
          { title: 'Total Sections', value: totalSections, icon: LayoutGrid, color: '#059669', bg: '#ECFDF5' },
          { title: 'Total Students', value: totalStudents.toLocaleString(), icon: Users, color: '#D97706', bg: '#FFFBEB' },
          { title: 'Assigned Teachers', value: assignedTeachers, icon: UserCheck, color: '#7C3AED', bg: '#F5F3FF' },
          { title: 'Available Seats', value: availableSeats, icon: Armchair, color: '#0891B2', bg: '#ECFEFF' },
          { title: 'Avg. Strength', value: `${avgStrength} / sec`, icon: Layers, color: '#6366F1', bg: '#EEF2FF' },
          { title: 'Seat Utilization', value: `${seatUtilization}%`, icon: Percent, color: seatUtilization > 90 ? '#DC2626' : '#16A34A', bg: seatUtilization > 90 ? '#FEF2F2' : '#F0FDF4' },
        ].map((kpi, idx) => (
          <div key={idx} style={styles.card} className="hover-card">
            <div style={{...styles.iconBox, backgroundColor: kpi.bg, color: kpi.color}}>
              <kpi.icon size={20} />
            </div>
            <h2 style={styles.cardNumber}>{kpi.value}</h2>
            <p style={styles.cardTitle}>{kpi.title}</p>
          </div>
        ))}
      </div>

      {/* 🔍 ADVANCED FILTERS */}
      <div style={styles.filtersContainer}>
        <div style={{ position: 'relative' }}>
          <Search size={16} color="#94A3B8" style={{ position: 'absolute', left: '14px', top: '12px' }} />
          <input 
            type="text" 
            placeholder="Search Class, Section, Room, Teacher..." 
            style={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select style={styles.select} value={academicYearFilter} onChange={(e) => setAcademicYearFilter(e.target.value)}>
          <option value="All">Academic Year: All</option>
          <option value="2026-2027">2026-2027</option>
          <option value="2025-2026">2025-2026</option>
        </select>

        <select style={styles.select} value={mediumFilter} onChange={(e) => setMediumFilter(e.target.value)}>
          <option value="All">Medium: All</option>
          <option value="English">English</option>
          <option value="Kannada">Kannada</option>
        </select>

        <select style={styles.select} value={shiftFilter} onChange={(e) => setShiftFilter(e.target.value)}>
          <option value="All">Shift: All</option>
          <option value="Morning">Morning</option>
          <option value="Afternoon">Afternoon</option>
        </select>

        <select style={styles.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="All">Status: All</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>

        <button 
          style={{...styles.btnSecondary, marginLeft: 'auto', color: '#64748B'}} 
          onClick={() => { setSearch(''); setStatusFilter('All'); setAcademicYearFilter('All'); setMediumFilter('All'); setShiftFilter('All'); }}
        >
          Reset Filters
        </button>
      </div>

      {/* 📋 BETTER CLASS TABLE */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{...styles.th, width: '40px'}}>
                <input type="checkbox" onChange={handleSelectAll} checked={selectedClasses.length === filteredClasses.length && filteredClasses.length > 0} />
              </th>
              <th style={styles.th}>Class & Section</th>
              <th style={styles.th}>Medium</th>
              <th style={styles.th}>Shift</th>
              <th style={styles.th}>Class Teacher</th>
              <th style={styles.th}>Students / Capacity</th>
              <th style={styles.th}>Capacity Visual</th>
              <th style={styles.th}>Room & Floor</th>
              <th style={styles.th}>Status</th>
              <th style={{...styles.th, textAlign: 'right'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClasses.length > 0 ? filteredClasses.map((cls) => {
              const isActive = cls.active;
              const enrollment = cls.currentEnrollment || 0;
              const capacity = cls.maxCapacity || 40;
              const fillPercent = Math.min(100, Math.round((enrollment / capacity) * 100));
              const isFull = fillPercent >= 100;
              const isHigh = fillPercent >= 90;

              const statusStyle = isActive 
                ? { bg: '#DCFCE7', color: '#166534', icon: <CheckCircle2 size={14}/>, text: 'Active' }
                : { bg: '#F1F5F9', color: '#475569', icon: <Clock size={14}/>, text: 'Inactive' };

              return (
                <tr key={cls.classId || cls.id} className="table-row">
                  <td style={styles.td}>
                    <input type="checkbox" checked={selectedClasses.includes(cls.classId || cls.id)} onChange={() => handleSelectOne(cls.classId || cls.id)} />
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: '700', color: '#0F172A', fontSize: '14px', cursor: 'pointer' }} onClick={() => openDrawer(cls)}>
                      {cls.className ? cls.className : 'Class'} {cls.section && !cls.className?.toLowerCase().includes(`section ${cls.section.toLowerCase()}`) && !cls.className?.toLowerCase().includes(`- ${cls.section.toLowerCase()}`) ? <span style={{ color: '#2563EB' }}>- Section {cls.section}</span> : ''}
                    </div>
                    <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>{cls.academicYear || '2026-2027'}</div>
                  </td>
                  <td style={styles.td}>
                    <span style={{ fontSize: '12px', background: '#F1F5F9', color: '#334155', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}>
                      {cls.medium || 'English'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ fontSize: '12px', color: '#475569' }}>{cls.shift || 'Morning'}</span>
                  </td>
                  <td style={styles.td}>
                    {cls.classTeacherName ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', color: '#1E293B' }}>
                        <UserCheck size={14} color="#2563EB" /> {cls.classTeacherName}
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#EF4444', fontStyle: 'italic' }}>Unassigned</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: isFull ? '#DC2626' : '#0F172A' }}>
                      {enrollment} <span style={{ fontSize: '12px', color: '#64748B', fontWeight: '500' }}>/ {capacity}</span>
                    </span>
                  </td>
                  {/* 🟢 CAPACITY VISUALIZATION BAR */}
                  <td style={styles.td}>
                    <div style={{ width: '100px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', fontWeight: '700', color: isHigh ? '#DC2626' : '#16A34A', marginBottom: '3px' }}>
                        <span>{fillPercent}%</span>
                        <span>{isFull ? 'FULL' : 'OK'}</span>
                      </div>
                      <div style={{ height: '6px', width: '100%', backgroundColor: '#E2E8F0', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${fillPercent}%`, backgroundColor: isFull ? '#DC2626' : isHigh ? '#F59E0B' : '#16A34A', borderRadius: '3px', transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: '13px', color: cls.roomNumber ? '#0F172A' : '#94A3B8', fontWeight: '600' }}>
                      {cls.roomNumber ? `Room ${cls.roomNumber}` : 'N/A'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>{cls.floor || 'Unspecified'}</div>
                  </td>
                  <td style={styles.td}>
                    <span style={{...styles.badge, backgroundColor: statusStyle.bg, color: statusStyle.color}}>
                      {statusStyle.icon} {statusStyle.text}
                    </span>
                  </td>
                  <td style={{...styles.td, textAlign: 'right'}}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                      <button onClick={() => openDrawer(cls)} style={{ padding: '6px 10px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '6px', cursor: 'pointer', color: '#2563EB', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }} title="Open Class Command Hub">
                        <Eye size={14} /> Dashboard
                      </button>
                      <button onClick={() => openAssignModalForClass(cls)} style={{ padding: '6px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#2563EB' }} title="Assign Class Teacher"><UserCheck size={18} /></button>
                      <button onClick={() => { if(onEdit) onEdit(cls); }} style={{ padding: '6px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#3B82F6' }} title="Edit"><Pencil size={18} /></button>
                      <button onClick={() => { if(onDeleteClass) onDeleteClass(cls); }} style={{ padding: '6px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#EF4444' }} title="Delete"><Trash size={18} /></button>
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="10" style={{ padding: '64px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>
                      <School size={32} />
                    </div>
                    <div>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#0F172A' }}>No classes found.</h3>
                      <p style={{ margin: 0, color: '#64748B' }}>Get started by creating your first class or importing from Excel.</p>
                    </div>
                    <button style={{...styles.btnPrimary, marginTop: '8px'}} onClick={onAddClass}><Plus size={18} /> Add First Class</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 📈 ENTERPRISE ANALYTICS & INSIGHTS */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
        {/* Student Enrollment Bar Chart */}
        <div style={{...styles.card, padding: '20px'}}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '700', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={18} color="#2563EB"/> Student Enrollment Breakdown
          </h3>
          <div style={{ height: '200px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={displayClasses.slice(0, 6)} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="className" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748B' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748B' }} />
                <RechartsTooltip cursor={{ fill: '#F1F5F9' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }} />
                <Bar dataKey="currentEnrollment" fill="#2563EB" radius={[4, 4, 0, 0]} barSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Capacity Utilization Pie Chart */}
        <div style={{...styles.card, padding: '20px'}}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '700', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Armchair size={18} color="#059669"/> Seat Capacity Distribution
          </h3>
          <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={[
                  { name: 'Filled Seats', value: totalStudents, color: '#2563EB' },
                  { name: 'Available Seats', value: availableSeats, color: '#CBD5E1' }
                ]} cx="50%" cy="50%" innerRadius={55} outerRadius={75} paddingAngle={2} dataKey="value" stroke="none">
                  <Cell fill="#2563EB" />
                  <Cell fill="#CBD5E1" />
                </Pie>
                <RechartsTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }} />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Operational Log */}
        <div style={{...styles.card, padding: '20px'}}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '700', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={18} color="#7C3AED"/> Operational Audit Log
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', height: '200px', overflowY: 'auto' }}>
            {dummyRecentActivity.map((activity) => (
              <div key={activity.id} style={{ display: 'flex', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: `${activity.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: activity.color, flexShrink: 0 }}>
                  <activity.icon size={16} />
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>{activity.action}</div>
                  <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>{activity.details}</div>
                  <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '3px' }}>{activity.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 👨🏫 TEACHER / ROLE ASSIGNMENT MODAL */}
      {assignModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '480px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', backgroundColor: '#EFF6FF', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <UserCheck size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#0F172A' }}>Assign Faculty Member</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748B' }}>Configure class, assistant, or subject teachers</p>
                </div>
              </div>
              <button onClick={() => setAssignModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}><X size={20} /></button>
            </div>

            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Role Type</label>
                <select style={{ ...styles.select, width: '100%', padding: '10px 12px' }} value={assignRole} onChange={(e) => setAssignRole(e.target.value)}>
                  <option value="Class Teacher">Class Teacher (Head Admin)</option>
                  <option value="Assistant Teacher">Assistant Teacher</option>
                  <option value="Subject Teacher">Subject Teacher</option>
                  <option value="Sports Teacher">Sports & PE Teacher</option>
                  <option value="Music Teacher">Music & Arts Teacher</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Target Class & Section</label>
                <select 
                  style={{ ...styles.select, width: '100%', padding: '10px 12px', backgroundColor: isClassLocked ? '#F1F5F9' : 'white', cursor: isClassLocked ? 'not-allowed' : 'pointer' }}
                  value={assignClassId}
                  disabled={isClassLocked}
                  onChange={(e) => setAssignClassId(e.target.value)}
                >
                  <option value="">-- Choose Class --</option>
                  {displayClasses.map(c => (
                    <option key={c.classId || c.id} value={c.classId || c.id}>
                      {c.className} - Section {c.section || 'A'} (Teacher: {c.classTeacherName || 'None'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Select Faculty Member</label>
                <select style={{ ...styles.select, width: '100%', padding: '10px 12px' }} value={assignTeacherName} onChange={(e) => setAssignTeacherName(e.target.value)}>
                  {teachers && teachers.length > 0 ? (
                    teachers.map((t, idx) => <option key={idx} value={t.teacherName || t.name}>{t.teacherName || t.name}{t.specialization || t.department ? ` (${t.specialization || t.department})` : ''}</option>)
                  ) : (
                    <option value="" disabled>No registered faculty in database</option>
                  )}
                </select>
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', gap: '12px', backgroundColor: '#F8FAFC' }}>
              <button onClick={() => setAssignModalOpen(false)} style={{ ...styles.btnSecondary, padding: '9px 18px' }}>Cancel</button>
              <button onClick={handleSaveTeacherAssignment} style={{ ...styles.btnPrimary, padding: '9px 20px' }}>Save Faculty Link</button>
            </div>
          </div>
        </div>
      )}

      {/* ✨ BULK PROMOTION MODAL */}
      {showPromoteModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '520px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', backgroundColor: '#F3E8FF', color: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Sparkles size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#0F172A' }}>Bulk Class Promotion Workflow</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748B' }}>Promote all students across classes to next academic year</p>
                </div>
              </div>
              <button onClick={() => setShowPromoteModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}><X size={20} /></button>
            </div>

            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ padding: '12px 14px', backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', fontSize: '13px', color: '#166534' }}>
                <strong>🎓 Academic Transition Rule:</strong><br/>
                • LKG → UKG<br/>
                • UKG → 1st Standard<br/>
                • 1st Standard → 2nd Standard<br/>
                • Enrolled students will be automatically migrated to their promoted section.
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Source Academic Year</label>
                <input type="text" className="form-control" value="2026-2027" disabled style={{ width: '100%', padding: '9px 12px', background: '#F1F5F9', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Target Promoted Year</label>
                <input type="text" className="form-control" defaultValue="2027-2028" style={{ width: '100%', padding: '9px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px' }} />
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', gap: '12px', backgroundColor: '#F8FAFC' }}>
              <button onClick={() => setShowPromoteModal(false)} style={{ ...styles.btnSecondary, padding: '9px 18px' }}>Cancel</button>
              <button onClick={handleBulkPromote} style={{ ...styles.btnPrimary, backgroundColor: '#8B5CF6', padding: '9px 20px' }}>Execute Bulk Promotion</button>
            </div>
          </div>
        </div>
      )}

      {/* 👨🎓 AUTO-BALANCE SECTIONS MODAL */}
      {showAutoBalanceModal && selectedClass && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '460px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', backgroundColor: '#EFF6FF', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ArrowRightLeft size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#0F172A' }}>Auto Balance Section Strength</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748B' }}>Distribute students evenly across {selectedClass.className} sections</p>
                </div>
              </div>
              <button onClick={() => setShowAutoBalanceModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}><X size={20} /></button>
            </div>

            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <p style={{ fontSize: '13px', color: '#475569', margin: 0 }}>
                This action will automatically balance student enrollment across all active sections of <strong>{selectedClass.className}</strong>.
              </p>
              <div style={{ padding: '14px', background: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '13px' }}>
                {displayClasses.filter(c => c.className === selectedClass.className).map((sec, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span>{sec.className} - Section {sec.section}</span>
                    <span style={{ fontWeight: '700', color: '#2563EB' }}>{sec.currentEnrollment} Students</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', gap: '12px', backgroundColor: '#F8FAFC' }}>
              <button onClick={() => setShowAutoBalanceModal(false)} style={{ ...styles.btnSecondary, padding: '9px 18px' }}>Cancel</button>
              <button onClick={handleAutoBalance} style={{ ...styles.btnPrimary, padding: '9px 20px' }}>Apply Auto-Balance</button>
            </div>
          </div>
        </div>
      )}

      {/* 👨🏫 CLASS COMMAND CENTER DRAWER & OVERLAY */}
      <div style={{...styles.overlay, ...(drawerOpen ? styles.overlayOpen : {})}} onClick={() => setDrawerOpen(false)} />
      
      <div style={{...styles.drawer, ...(drawerOpen ? styles.drawerOpen : {})}}>
        {selectedClass && (
          <>
            {/* Drawer Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0F172A' }}>
                    {selectedClass.className ? selectedClass.className : 'Class'} {selectedClass.section && !selectedClass.className?.toLowerCase().includes(`section ${selectedClass.section.toLowerCase()}`) && !selectedClass.className?.toLowerCase().includes(`- ${selectedClass.section.toLowerCase()}`) ? <span style={{ color: '#2563EB' }}>- Section {selectedClass.section}</span> : ''}
                  </h2>
                  <span style={{ backgroundColor: '#DCFCE7', color: '#166534', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}>Active</span>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '4px', fontSize: '12px', color: '#64748B' }}>
                  <span>Medium: <strong>{selectedClass.medium || 'English'}</strong></span>
                  <span>•</span>
                  <span>Shift: <strong>{selectedClass.shift || 'Morning'}</strong></span>
                  <span>•</span>
                  <span>{selectedClass.academicYear || '2026-2027'}</span>
                </div>
              </div>
              <button onClick={() => setDrawerOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '50%', color: '#64748B' }}>
                <X size={22} />
              </button>
            </div>

            {/* Sub-Navigation Tabs in Command Hub */}
            <div style={{ display: 'flex', borderBottom: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', padding: '0 24px' }}>
              {[
                { id: 'overview', label: 'Command Overview', icon: LayoutGrid },
                { id: 'infrastructure', label: 'Room & Tech', icon: Building },
                { id: 'timetable', label: 'Timetable', icon: Calendar },
                { id: 'documents', label: 'Documents', icon: FileText },
              ].map(tab => {
                const Icon = tab.icon;
                const isActive = activeDrawerTab === tab.id;
                return (
                  <button 
                    key={tab.id}
                    onClick={() => setActiveDrawerTab(tab.id)}
                    style={{
                      padding: '12px 16px',
                      background: 'none',
                      border: 'none',
                      borderBottom: isActive ? '3px solid #2563EB' : '3px solid transparent',
                      color: isActive ? '#2563EB' : '#64748B',
                      fontWeight: isActive ? '700' : '600',
                      fontSize: '13px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Icon size={15} /> {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Drawer Body Content */}
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#FAFAFA' }}>
              
              {/* TAB 1: OVERVIEW HUB */}
              {activeDrawerTab === 'overview' && (
                <div>
                  {/* KPI Mini Summary */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ background: 'white', padding: '12px', borderRadius: '10px', border: '1px solid #E2E8F0', textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#64748B', fontWeight: '600' }}>Students</div>
                      <div style={{ fontSize: '18px', fontWeight: '800', color: '#2563EB' }}>{selectedClass.currentEnrollment || 0}</div>
                    </div>
                    <div style={{ background: 'white', padding: '12px', borderRadius: '10px', border: '1px solid #E2E8F0', textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#64748B', fontWeight: '600' }}>Boys / Girls</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>
                        {selectedClass.boys !== undefined ? selectedClass.boys : Math.floor((selectedClass.currentEnrollment || 0) * 0.5)} B / {selectedClass.girls !== undefined ? selectedClass.girls : (selectedClass.currentEnrollment || 0) - Math.floor((selectedClass.currentEnrollment || 0) * 0.5)} G
                      </div>
                    </div>
                    <div style={{ background: 'white', padding: '12px', borderRadius: '10px', border: '1px solid #E2E8F0', textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#64748B', fontWeight: '600' }}>Attendance</div>
                      <div style={{ fontSize: '18px', fontWeight: '800', color: (selectedClass.currentEnrollment || 0) > 0 ? '#16A34A' : '#64748B' }}>
                        {(selectedClass.currentEnrollment || 0) > 0 ? (selectedClass.attendanceToday || '100%') : 'N/A'}
                      </div>
                    </div>
                    <div style={{ background: 'white', padding: '12px', borderRadius: '10px', border: '1px solid #E2E8F0', textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#64748B', fontWeight: '600' }}>Fees Pending</div>
                      <div style={{ fontSize: '18px', fontWeight: '800', color: (selectedClass.feesPending || 0) > 0 ? '#DC2626' : '#16A34A' }}>
                        {selectedClass.feesPending || 0}
                      </div>
                    </div>
                  </div>

                  {/* Teacher & Command Details */}
                  <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>Faculty & Command Links</h4>
                      <button onClick={() => openAssignModalForClass(selectedClass)} style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#2563EB', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                        + Change Teacher
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', fontSize: '13px' }}>
                      <div>
                        <span style={{ color: '#64748B' }}>Class Teacher:</span><br/>
                        <strong style={{ color: '#0F172A' }}>{selectedClass.classTeacherName || 'Not Assigned'}</strong>
                      </div>
                      <div>
                        <span style={{ color: '#64748B' }}>Assistant Teacher:</span><br/>
                        <strong style={{ color: '#0F172A' }}>{selectedClass.assistantTeacher || 'Not Assigned'}</strong>
                      </div>
                      <div>
                        <span style={{ color: '#64748B' }}>Transport Users:</span><br/>
                        <strong style={{ color: '#2563EB' }}>{selectedClass.transportCount || 0} Enrolled Bus Riders</strong>
                      </div>
                      <div>
                        <span style={{ color: '#64748B' }}>Library Issued Books:</span><br/>
                        <strong style={{ color: '#7C3AED' }}>{selectedClass.libraryBooks || 0} Active Books Issued</strong>
                      </div>
                    </div>
                  </div>

                  {/* Student Allocation & Section Balancing Quick Button */}
                  <div style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)', border: '1px solid #BFDBFE', borderRadius: '12px', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 style={{ margin: '0 0 2px 0', fontSize: '14px', fontWeight: '700', color: '#1E40AF' }}>Section Student Allocation</h4>
                      <p style={{ margin: 0, fontSize: '12px', color: '#3B82F6' }}>Auto-balance section strength with 1-click intelligent distribution.</p>
                    </div>
                    <button onClick={() => setShowAutoBalanceModal(true)} style={{ ...styles.btnPrimary, backgroundColor: '#2563EB' }}>
                      <ArrowRightLeft size={15} /> Auto-Balance
                    </button>
                  </div>
                </div>
              )}

              {/* TAB 2: ROOM & INFRASTRUCTURE */}
              {activeDrawerTab === 'infrastructure' && (
                <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '700', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Building size={18} color="#2563EB"/> Classroom Infrastructure Profile
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '13px' }}>
                    <div style={{ background: '#F8FAFC', padding: '12px', borderRadius: '8px' }}>
                      <span style={{ color: '#64748B' }}>Room Number</span>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: selectedClass.roomNumber ? '#0F172A' : '#64748B' }}>
                        {selectedClass.roomNumber ? `Room ${selectedClass.roomNumber}` : 'Not Specified'}
                      </div>
                    </div>
                    <div style={{ background: '#F8FAFC', padding: '12px', borderRadius: '8px' }}>
                      <span style={{ color: '#64748B' }}>Floor Level</span>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: selectedClass.floor ? '#0F172A' : '#64748B' }}>
                        {selectedClass.floor || 'Not Specified'}
                      </div>
                    </div>
                    <div style={{ background: '#F8FAFC', padding: '12px', borderRadius: '8px' }}>
                      <span style={{ color: '#64748B' }}>Building Wing</span>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: selectedClass.building ? '#0F172A' : '#64748B' }}>
                        {selectedClass.building || 'Not Specified'}
                      </div>
                    </div>
                    <div style={{ background: '#F8FAFC', padding: '12px', borderRadius: '8px' }}>
                      <span style={{ color: '#64748B' }}>Smart Board / Projector</span>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: selectedClass.hasSmartBoard ? '#16A34A' : '#64748B', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                        <Monitor size={16}/> {selectedClass.hasSmartBoard ? 'Installed & Active' : 'Not Configured'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 3: TIMETABLE INTEGRATION */}
              {activeDrawerTab === 'timetable' && (
                <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Calendar size={18} color="#2563EB"/> Weekly Class Schedule
                    </h4>
                    <span style={{ fontSize: '12px', background: '#F1F5F9', padding: '4px 10px', borderRadius: '6px', color: '#475569', fontWeight: '600' }}>
                      {selectedClass.timetableSlots && selectedClass.timetableSlots.length > 0 ? `${selectedClass.timetableSlots.length} Periods Configured` : 'Live Schedule'}
                    </span>
                  </div>
                  {selectedClass.timetableSlots && selectedClass.timetableSlots.length > 0 ? (
                    <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => {
                        const daySlots = selectedClass.timetableSlots.filter(s => s.dayOfWeek === day || s.day === day);
                        return (
                          <div key={day} style={{ flex: 1, minWidth: '100px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                            <div style={{ fontWeight: '700', fontSize: '12px', color: '#2563EB', marginBottom: '6px' }}>{day.substring(0, 3)}</div>
                            {daySlots.length > 0 ? daySlots.map((slot, idx) => (
                              <div key={idx} style={{ fontSize: '11px', color: '#334155', marginTop: '4px' }}>
                                <strong>{slot.subjectName || slot.subject}</strong> • {slot.startTime || '9:00'}
                              </div>
                            )) : (
                              <div style={{ fontSize: '11px', color: '#94A3B8', fontStyle: 'italic' }}>No Class</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '32px 16px', background: '#F8FAFC', borderRadius: '8px', border: '1px dashed #CBD5E1' }}>
                      <Calendar size={28} color="#94A3B8" style={{ marginBottom: '8px' }} />
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>No published timetable for {selectedClass.className} (Section {selectedClass.section || 'A'}).</div>
                      <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>Configure and publish weekly period slots in Academics → Time Table.</div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 4: CLASS DOCUMENTS */}
              {activeDrawerTab === 'documents' && (
                <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                  <h4 style={{ margin: '0 0 14px 0', fontSize: '15px', fontWeight: '700', color: '#0F172A' }}>Class Documents & Materials</h4>
                  {selectedClass.documents && selectedClass.documents.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {selectedClass.documents.map((doc, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '13px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}>
                            <FileText size={16} color="#2563EB" /> {doc.name}
                          </div>
                          <span style={{ fontSize: '12px', color: '#94A3B8' }}>{doc.size || 'Attachment'}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '32px 16px', background: '#F8FAFC', borderRadius: '8px', border: '1px dashed #CBD5E1' }}>
                      <FileText size={28} color="#94A3B8" style={{ marginBottom: '8px' }} />
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>No documents uploaded for this class.</div>
                      <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>Syllabus and lesson plan attachments will appear here.</div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Drawer Footer Actions */}
            <div style={{ padding: '20px 24px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
              <button onClick={() => { if (onEdit) onEdit(selectedClass); setDrawerOpen(false); }} style={{ ...styles.btnSecondary, color: '#2563EB', borderColor: '#BFDBFE' }}>
                <Pencil size={15}/> Edit Details
              </button>
              <button onClick={() => setDrawerOpen(false)} style={{ ...styles.btnPrimary, padding: '9px 24px' }}>Close</button>
            </div>
          </>
        )}
      </div>
      
      <style>{`
        .hover-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
        }
        .table-row:hover td {
          background-color: #F8FAFC;
        }
      `}</style>
    </div>
  );
};

export default ClassManagement;
